// "Adicionar" de TODO card de produto (ds-product-card): coleção, busca, página do veículo e o
// "Complete o seu veículo" da gaveta e da /cart. Carregado em toda página pelo ds-head — e não pelo
// ds-cart.js, que só existe onde há gaveta ou na /cart: no plano B (popup do Dawn, cart_type
// notification) o botão da coleção ficaria mudo. Para onde vai o redesenho quem decide é o núcleo
// (destinoDoAdicionar, testado); aqui só rede e DOM.
//
// O listener é registrado JÁ na avaliação do módulo, e o núcleo é esperado DENTRO do clique: os
// botões vêm prontos do servidor, e com um `await import` no topo um toque logo que a página
// aparece se perdia sem sinal nenhum (revisão de 2026-09-28).
const nucleo = window.ArpineDS.importar("carrinho");
nucleo.catch(() => {}); // sem "unhandled rejection" antes do 1º clique; o clique mostra o erro

// O CartItems do Dawn IGNORA esta origem. Com qualquer outra, o onCartUpdate dele (da /cart e da
// gaveta, que é uma subclasse) buscaria de novo o que acabamos de desenhar. A barra inferior e o
// ds-cart.js escutam o evento igual.
const ORIGEM = "cart-items";
const ERRO_PADRAO = "Não deu para adicionar agora. Tenta de novo.";

const urlDoCarrinho = () => window.routes?.cart_url ?? "/cart";

// Espera o Dawn promover o elemento, com limite: se o script dele nunca vier, o botão avisa o
// erro em vez de ficar desabilitado para sempre.
const promovido = (tag) =>
  Promise.race([
    customElements.whenDefined(tag),
    new Promise((_, rejeitar) => setTimeout(() => rejeitar(new Error(`${tag} não definido`)), 5000)),
  ]);

// O carrinho de verdade, lido do /cart.json pelo leitor do próprio Dawn quando ele existe.
const lerCarrinho = () =>
  (typeof CartItems !== "undefined"
    ? CartItems.fetchCartData()
    : fetch(`${urlDoCarrinho()}.json`).then((r) => r.json())
  ).catch(() => null);

// Os mesmos eventos de analytics que o product-form do Dawn dispara num add (StandardEvents do
// Shopify): sem eles, o "Adicionar" do card não contaria como "adicionou ao carrinho". A promessa
// do CartLinesUpdateEvent é resolvida ou rejeitada EXATAMENTE uma vez, em todo caminho.
function eventosDoAdd(botao, varianteId) {
  const { CartLinesUpdateEvent, CartErrorEvent } = window.StandardEvents || {};
  const erroNoAnalytics = (mensagem) => {
    if (CartErrorEvent) botao.dispatchEvent(new CartErrorEvent({ error: mensagem, code: "INVALID" }));
  };
  if (!CartLinesUpdateEvent) return { resolver() {}, rejeitar: erroNoAnalytics };
  const adiado = CartLinesUpdateEvent.createPromise();
  botao.dispatchEvent(
    new CartLinesUpdateEvent({
      action: "add",
      context: "product",
      lines: [{ merchandiseId: varianteId, quantity: 1 }],
      promise: adiado.promise,
    })
  );
  let fechado = false;
  return {
    resolver(cart) {
      if (fechado) return;
      fechado = true;
      try {
        if (!cart?.currency) throw new Error("Missing currency in cart response");
        adiado.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      } catch (e) {
        adiado.reject(e); // analytics nunca derruba o fluxo (nem a ida para a /cart)
      }
    },
    rejeitar(mensagem) {
      erroNoAnalytics(mensagem);
      if (fechado) return;
      fechado = true;
      adiado.reject(new Error(mensagem));
    },
  };
}

document.addEventListener("click", async (e) => {
  const botao = e.target.closest?.("[data-ds-adicionar]");
  if (!botao || botao.disabled) return;
  botao.disabled = true; // duplo clique não adiciona duas vezes
  const erro = botao.parentElement.querySelector("[data-ds-adicionar-erro]");
  if (erro) erro.hidden = true;
  const falhar = (mensagem) => {
    if (erro) {
      erro.textContent = mensagem;
      erro.hidden = false;
    }
    botao.disabled = false;
  };

  let destino, gaveta, lista;
  try {
    const { destinoDoAdicionar } = await nucleo;
    gaveta = document.querySelector("cart-drawer");
    lista = document.querySelector("cart-items");
    // Um toque logo que a página aparece pode chegar antes do cart-drawer.js/cart.js do Dawn
    // promoverem o elemento: sem os métodos dele, esperar a promoção em vez de quebrar.
    if (gaveta) await promovido("cart-drawer");
    if (lista) await promovido("cart-items");
    destino = destinoDoAdicionar({
      contexto: botao.closest("[data-ds-cart-sugestoes]")?.dataset.contexto,
      temGaveta: Boolean(gaveta),
      temListaDaPagina: Boolean(lista),
    });
  } catch {
    falhar(ERRO_PADRAO); // núcleo não carregou: o botão avisa em vez de ficar mudo
    return;
  }
  // As seções e o leitor de HTML são os do próprio Dawn: gaveta → cart-drawer.getSectionsToRender()
  // (ids "cart-drawer" e "cart-icon-bubble"); página → cart-items.getSectionsToRender() (o nome de
  // cada seção está no campo section).
  const secoes = destino === "gaveta" ? gaveta.getSectionsToRender() : destino === "pagina" ? lista.getSectionsToRender() : [];
  const varianteId = botao.dataset.varianteId;
  const eventos = eventosDoAdd(botao, varianteId);

  let json = null;
  try {
    const r = await fetch(`${window.routes?.cart_add_url ?? "/cart/add"}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(varianteId), quantity: 1 }],
        sections: secoes.map((s) => (destino === "gaveta" ? s.id : s.section)),
        sections_url: location.pathname,
      }),
    });
    json = await r.json().catch(() => null);
    if (!r.ok || !json || json.status) throw new Error("recusado");
  } catch {
    // Só a mensagem do próprio Shopify (em português, ex.: produto esgotado) vai para a tela;
    // falha de rede ou de leitura vira a frase padrão — nunca o "Failed to fetch" do navegador.
    const mensagem = json?.description || json?.message || ERRO_PADRAO;
    eventos.rejeitar(mensagem);
    falhar(mensagem);
    return;
  }

  // Daqui para baixo o produto JÁ ESTÁ no carrinho.
  if (destino === "carrinho") {
    // Sem gaveta nem lista para redesenhar (plano B: o popup do Dawn, que só o product-form sabe
    // abrir): a /cart, onde o item aparece de certeza.
    eventos.resolver(await lerCarrinho());
    location.assign(urlDoCarrinho());
    return;
  }

  // Se redesenhar falhar, recarregar a página — nunca reabilitar o botão sem redesenho (um segundo
  // clique adicionaria de novo sem a pessoa ver o primeiro).
  try {
    if (destino === "gaveta") {
      const jaAberta = gaveta.classList.contains("active");
      // Card fora da gaveta (coleção, busca...): fechar a gaveta devolve o foco a este botão.
      if (!jaAberta) gaveta.setActiveElement(botao);
      gaveta.classList.remove("is-empty");
      gaveta.renderContents(json);
      // Gaveta fechada: o open() do Dawn prende o foco sozinho no fim da animação. Já aberta (card
      // do "Complete" lá dentro), o open() volta cedo: sem isto o foco ficaria no botão que sumiu e
      // o Tab sairia da gaveta (aria-modal). O item novo é o 1º.
      if (jaAberta) setTimeout(() => trapFocus(document.getElementById("CartDrawer"), gaveta.querySelector(".cart-item__name")));
    } else {
      // O mesmo que o updateQuantity do Dawn faz com a resposta.
      for (const s of secoes) {
        const el = document.getElementById(s.id);
        const alvo = el?.querySelector(s.selector) || el;
        if (alvo && json.sections?.[s.section] != null) alvo.innerHTML = lista.getSectionInnerHTML(json.sections[s.section], s.selector);
      }
      lista.classList.remove("is-empty");
      document.getElementById("main-cart-footer")?.classList.remove("is-empty");
      document.querySelector("#main-cart-items .cart-item__name")?.focus();
    }
  } catch {
    eventos.resolver(await lerCarrinho()); // o add aconteceu; só o redesenho falhou
    location.reload();
    return;
  }
  // O botão de um card da coleção, da busca ou da página do veículo continua na tela: pode
  // adicionar outra unidade, como o botão da PDP. O do "Complete" NÃO volta: na gaveta ele some
  // no redesenho, e na /cart fica até o lugar se refazer (um 2º toque ali adicionaria de novo).
  if (!botao.closest("[data-ds-cart-sugestoes]")) botao.disabled = false;

  // cartData de verdade: o quick-add-bulk do Dawn lê event.cartData.items sem checar (aviso no
  // standard-actions-override.js).
  const cartData = await lerCarrinho();
  eventos.resolver(cartData);
  if (typeof publish === "function" && cartData) publish(PUB_SUB_EVENTS.cartUpdate, { source: ORIGEM, cartData });
});

// Plano B: o "Adicionar" leva para a /cart com o botão desabilitado; voltando pelo histórico, o
// navegador devolve a página congelada (bfcache) com o botão morto. Reativa ao voltar.
window.addEventListener("pageshow", (e) => {
  if (!e.persisted) return;
  for (const b of document.querySelectorAll("[data-ds-adicionar][disabled]")) b.disabled = false;
});
